import "./BundleSelectorDropdown.scss";

import React, { useEffect, useState } from "react";
import { Dropdown, MenuItem } from "react-bootstrap";
import { withReactAdapter } from "../../module-federation/withReactAdapter";
import { BettyBundle } from "../../types/EvaluateArticleNew";
import { FormattedMessage } from "react-intl";
import MoneyAmount from "../PriceDisplay/MoneyAmount";
import { PriceDisplayData, PricesDisplayData } from "../../types/PriceAndPromotionInfo";
import { getIntlFormatingForPhraseProject } from "../../i18n/IntlRootMixin";
import { CompulsoryTrackingTags, TrackingTags } from "../../tracking/Tracking";
import { trackSelectBundle } from "../../utils/ArticleTrackingUtil";
import { trackBundleSelectorToggled } from "../../utils/SearchTrackingUtil";
import ErrorBoundary from "../ErrorBoundary";
import { jsxWithIntl } from "../../i18n/IntlRootMixin";

type Props = {
    selectedBundleNumber: string;
    bundles?: Record<string, BettyBundle>;
    pricesDisplayData: PricesDisplayData | null;
    bundleSelector?: Record<string, string>;
    onBundleSelect: (selectedBettyBundleId: string) => void;
    compulsoryTrackingTags: CompulsoryTrackingTags;
    optionalTrackingTags?: Partial<TrackingTags>;
};

type BundleInfo = {
    bundleNumber: string;
    packagingType: string;
    bundle: BettyBundle;
    isSelectedBundle: boolean;
    isWeightedArticle: boolean;
    priceDisplayData: PriceDisplayData | null;
    compulsoryTrackingTags: CompulsoryTrackingTags;
    optionalTrackingTags: Partial<TrackingTags> | undefined;
};

type State = {
    currentlySelectedPackagingType: string | null;
    bundleInfosList: BundleInfo[];
};

/**
 * Formats the packaging type text with weight info if available
 */
function getFormattedPackagingType(bundle: BettyBundle, defaultPackagingType: string): string {
    // Workaround for wrongly set packaging types in EV's `variant.bundleSelector` field
    // for weighted articles. Needs to be in place until BTEV-67145 is finished.
    const weightPerPiece = bundle?.weightPerPiece ?? null;
    
    if (!weightPerPiece) {
        return defaultPackagingType;
    }
    
    const intlFormatting = getIntlFormatingForPhraseProject("EXPLORE");
    return intlFormatting.formatMessage(
        { id: "EX_WEIGHT_PER_PIECES" },
        {
            weightPerPiece: intlFormatting.formatNumber(weightPerPiece.value),
            unitOfMeasurement: weightPerPiece?.uom?.toLowerCase()
        }
    );
}

/**
 * Processes bundles data and builds component state
 */
function computeState(
    selectedBundleNumber: string,
    bettyBundles: Record<string, BettyBundle> | undefined,
    bundleSelector: Record<string, string> | undefined,
    pricesDisplaysData: PricesDisplayData | null,
    compulsoryTrackingTags: CompulsoryTrackingTags,
    optionalTrackingTags: Partial<TrackingTags> | undefined
): State {
    const bundleInfosList: BundleInfo[] = [];
    let currentlySelectedPackagingType: string | null = null;
    
    if (!bettyBundles || !bundleSelector) {
        return { currentlySelectedPackagingType, bundleInfosList };
    }

    // Process each bundle in the selector
    for (const bundleNumber of Object.keys(bundleSelector)) {
        const bundle = bettyBundles[bundleNumber];
        if (!bundle) continue;

        const isSelectedBundle = selectedBundleNumber === bundleNumber;
        const isWeightedArticle = bundle.isWeightArticle === "WEIGHT";
        const priceDisplayData = pricesDisplaysData?.[bundleNumber] || null;
        const packagingType = getFormattedPackagingType(bundle, bundleSelector[bundleNumber]);

        const bundleInfo: BundleInfo = {
            bundleNumber,
            packagingType,
            bundle,
            isSelectedBundle,
            isWeightedArticle,
            priceDisplayData,
            compulsoryTrackingTags,
            optionalTrackingTags
        };
        
        bundleInfosList.push(bundleInfo);

        if (isSelectedBundle) {
            currentlySelectedPackagingType = bundleInfo.packagingType;
        }
    }
    
    return { currentlySelectedPackagingType, bundleInfosList };
}

export function BundleSelectorDropdown({
    selectedBundleNumber,
    bundles,
    pricesDisplayData,
    bundleSelector,
    onBundleSelect,
    compulsoryTrackingTags,
    optionalTrackingTags
}: Props) {
    const [state, setState] = useState<State>(
        computeState(
            selectedBundleNumber,
            bundles,
            bundleSelector,
            pricesDisplayData,
            compulsoryTrackingTags,
            optionalTrackingTags
        )
    );

    useEffect(() => {
        setState(
            computeState(
                selectedBundleNumber,
                bundles,
                bundleSelector,
                pricesDisplayData,
                compulsoryTrackingTags,
                optionalTrackingTags
            )
        );
    }, [selectedBundleNumber, bundles, bundleSelector, pricesDisplayData, compulsoryTrackingTags, optionalTrackingTags]);

    /**
     * Handles bundle selection with tracking
     */
    const handleBundleSelect = (
        bettyBundle: BettyBundle,
        compulsoryTrackingTags: CompulsoryTrackingTags,
        optionalTrackingTags?: Partial<TrackingTags>
    ) => {
        const bundleId = bettyBundle.bettyBundleId?.bettyBundleId;
        if (bundleId) {
            onBundleSelect(bundleId);
            trackSelectBundle(bundleId, compulsoryTrackingTags, optionalTrackingTags);
        }
    };

    /**
     * Tracks dropdown toggle state
     */
    const handleBundleSelectorToggled = (isOpen: boolean, source: string) => {
        trackBundleSelectorToggled(
            isOpen,
            optionalTrackingTags?.originalBettyVariantBundleId,
            optionalTrackingTags?.bettyVariantId,
            optionalTrackingTags?.bettyVariantBundleIds,
            source
        );
    };

    /**
     * Renders dropdown menu items for each bundle
     */
    const renderMenuItems = () => {
        return state.bundleInfosList.map((bundleInfo) => {
            const bundleId = bundleInfo.bundle.bettyBundleId?.bettyBundleId;
            if (!bundleId) return null;
            
            return (
                <MenuItem
                    key={`bundle${bundleInfo.bundle.bundleId?.bettyBundleId}`}
                    active={bundleInfo.isSelectedBundle}
                    onSelect={() => handleBundleSelect(
                        bundleInfo.bundle,
                        bundleInfo.compulsoryTrackingTags,
                        bundleInfo.optionalTrackingTags
                    )}>
                    <span className="packaging-type">{bundleInfo.packagingType}</span>
                    <span className={bundleInfo.isSelectedBundle ? "icon-check" : ""} />
                    <span className="price">
                        {bundleInfo.priceDisplayData?.primary?.price &&
                            bundleInfo.priceDisplayData?.currency && (
                                <MoneyAmount
                                    price={bundleInfo.priceDisplayData.primary.price}
                                    currency={bundleInfo.priceDisplayData.currency}
                                    isWeightArticle={bundleInfo.isWeightedArticle}
                                    unitIndicatorWeight={bundleInfo.priceDisplayData.primary.unitIndicatorWeight}
                                    messageTranslationKey={bundleInfo.priceDisplayData.primary.info}
                                    className="price"
                                />
                            )}
                    </span>
                </MenuItem>
            );
        });
    };

    // Determine appropriate message based on number of bundles
    const moreBundlesMessageKey =
        state.bundleInfosList.length === 2 
            ? "SD_BUNDLE_SELECTOR_ONE_MORE_BUNDLE" 
            : "SD_BUNDLE_SELECTOR_X_MORE_BUNDLES";

    // Only show dropdown if we have multiple bundles
    const showDropdown = state.bundleInfosList.length > 1;
    
    return (
        <ErrorBoundary>
            {jsxWithIntl(
                <div className="sd-shared--bundle-selector">
                    <label>
                        <FormattedMessage id="SD_BUNDLE_SELECTOR_LABEL" />
                    </label>
                    <div className="inline-display">
                        <div className="bundle packaging-type pill">{state.currentlySelectedPackagingType}</div>
                        {showDropdown && (
                            <Dropdown
                                id="bundle-selector"
                                onToggle={(isOpen: boolean, event, metadata) => 
                                    handleBundleSelectorToggled(isOpen, metadata.source)
                                }>
                                <Dropdown.Toggle id="dropdown-basic" className="bundle dropdown pill">
                                    <FormattedMessage
                                        id={moreBundlesMessageKey}
                                        values={{ number: state.bundleInfosList.length - 1 }}
                                    />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {renderMenuItems()}
                                </Dropdown.Menu>
                            </Dropdown>
                        )}
                    </div>
                </div>
            )}
        </ErrorBoundary>
    );
}

export default withReactAdapter(BundleSelectorDropdown);
